/**
 * Le funzioni di START, come valore di ripiego.
 *
 * [sc-48351] Fino alla story 48351 questi erano gli interruttori di tutto il
 * gestionale. Ora ogni azienda ha un piano, e cosa può fare lo calcola l'API
 * (`entitlementsFor()` in `crmstart-api/src/config/entitlements.js`), che lo
 * manda col profilo. Le pagine lo leggono dallo store
 * (`company/features`), non da qui.
 *
 * Questo file serve a due cose: dà i nomi delle funzioni che il web conosce,
 * e vale finché il profilo non è arrivato. Per questo i valori sono quelli di
 * START: in attesa del profilo non si accende niente di PLUS.
 */
export const FEATURES = Object.freeze({
  /**
   * Modificatore prezzi sui preventivi: decremento e incremento percentuale.
   * Spento dalla task 48395 (`quote/priceAdjustment/:id`).
   */
  priceModifiers: false,

  /**
   * Modale «Gestisci pagamenti» sugli ordini: storico, acconto, saldi e
   * relative ricevute PDF. Spenta dalla task 48395 (`quote/:id/payments`),
   * riaccesa dalla story 48692 con due sole voci, acconto e saldo.
   */
  orderPayments: true,

  /**
   * Documenti di trasporto: generazione DDT, storico consegne, resi e ritiri.
   * Spenti dalla task 48394 (`/ddt/*` e `upload/ddts/:ddtId/documents`).
   */
  ddt: false,

  /**
   * DDT standard: un documento per ordine, a consegna intera, senza scelta
   * di causale o quantità. [sc-48692] Acceso in START con `ddt` spento: la
   * modale si apre al passaggio a «In consegna», e sull'ordine c'è «Genera
   * DDT» finché non esiste, «Scarica DDT» dopo. Gemello nell'API.
   */
  ddtStandard: true,

  /**
   * Stato spedizione «Consegna Parziale». Vive e muore col DDT: il DDT di
   * consegna era l'unico modo di scaricare il magazzino un pezzo per volta.
   * Spento dalla task 48394; la spedizione resta «In magazzino» →
   * «In consegna» → «Consegnato», che scarica per intero.
   */
  partialDelivery: false,

  /**
   * Stato pagamento «Pagato Parzialmente». Voleva dire «è arrivato un
   * acconto», e l'acconto era parte della gestione pagamenti spenta dalla
   * 48395. Spento dalla task 48465; restano «In attesa» e «Pagato».
   * Attenzione: con questo spento **per spedire serve «Pagato»**, perché il
   * parziale era anche ciò che sbloccava la colonna spedizione.
   */
  partialPayment: false,

  /**
   * Saldo parziale fra le voci della modale pagamenti. [sc-48692] Spento in
   * START: si registrano acconto e saldo, e il saldo paga esattamente il resto.
   */
  partialSettlements: false,

  /**
   * Il cliente aggiunge da sé i propri operatori (form «Aggiungi admin» in
   * Account). Spento dalla story 48692: li crea liberotratto dalla console,
   * con l'invito via email. Gemello in `crmstart-api/src/config/features.js`.
   */
  customerCreatesOperators: false,

  /**
   * La pagina «Log attività». [sc-48351 / task 48782] Spenta in START: le
   * righe si scrivono comunque, per tutte le aziende, ma a leggerle è PLUS.
   * Gemello in `crmstart-api/src/config/features.js`.
   */
  activityLog: false,

  /**
   * La pagina «Variazioni di prezzo» dentro i Listini: cambia il prezzo di
   * molti articoli in una volta, con anteprima, storico e annullamento.
   * [sc-48351 / task 48783] Spenta in START. Interruttore a parte da
   * `priceModifiers`, che accende invece la vecchia modale del singolo
   * preventivo. Gemello in `crmstart-api/src/config/features.js`.
   */
  priceListAdjustments: false,

  /**
   * I numeri sulla home: quanti preventivi, quanti ordini, chi compra, cosa
   * c'è in magazzino. [sc-48351] Spenti in START, dove la home resta l'elenco
   * di scorciatoie di sempre. Gemello in `crmstart-api/src/config/features.js`.
   */
  homeStats: false,

  /**
   * Ruoli e permessi: la pagina «Ruoli», la tendina del ruolo in Account e le
   * pagine e i bottoni che seguono il ruolo. [sc-48351 / task 48786] Spenti
   * in START, dove sono tutti amministratori. Senza questa riga il web
   * scarterebbe `roles` dal profilo: `leggiPiano` tiene solo i nomi elencati
   * qui. Gemello in `crmstart-api/src/config/features.js`.
   */
  roles: false,

  /**
   * Bozze dei preventivi: un preventivo si salva senza essere definitivo e
   * porta lo stato di dove è arrivato (§2.7). Accese, in Preventivi compare la
   * scheda «Bozze» e la colonna dello stato. Gemello nell'API.
   */
  quoteDrafts: false,

  /**
   * [sc-48139] Fatturazione elettronica (solo PLUS). Il valore vero arriva dal
   * profilo dell'azienda.
   */
  invoicing: false,
});

export default FEATURES;
